import React from 'react';
import {motion} from 'framer-motion';
import {format} from 'date-fns';
import {useContacts} from '../../contexts/ContactContext';
import SafeIcon from '../../common/SafeIcon';
import { FiEdit, FiEye, FiMail, FiPhone, FiTrash2 } from 'react-icons/fi';


const ContactList = ({contacts, onEditContact, onViewContact}) => {
  const {selectedContacts, setSelectedContacts, deleteContact} = useContacts();

  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(c => c.id));
    }
  };

  const getRecruitmentStageColor = (stage) => {
    const colors = {
      lead: 'bg-gray-100 text-gray-800',
      contacted: 'bg-blue-100 text-blue-800',
      interested: 'bg-green-100 text-green-800',
      nurturing: 'bg-yellow-100 text-yellow-800',
      meeting_scheduled: 'bg-purple-100 text-purple-800',
      converted: 'bg-emerald-100 text-emerald-800',
      not_interested: 'bg-red-100 text-red-800'
    };
    return colors[stage] || colors.lead;
  };

  const getRecruitmentStageLabel = (stage) => {
    const labels = {
      lead: 'Lead',
      contacted: 'Contacted',
      interested: 'Interested',
      nurturing: 'Nurturing',
      meeting_scheduled: 'Meeting Scheduled',
      converted: 'Converted',
      not_interested: 'Not Interested'
    };
    return labels[stage] || 'Lead';
  };

  if (contacts.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <SafeIcon icon={FiPhone} className="text-gray-300 text-4xl mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No contacts found</h3>
        <p className="text-gray-500">Get started by adding your first contact or importing from a CSV file.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedContacts.length === contacts.length && contacts.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Phone
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Recruitment Stage
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Contact
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contacts.map((contact, index) => (
              <motion.tr
                key={contact.id}
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{delay: index * 0.05}}
                className="table-row"
              >
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedContacts.includes(contact.id)}
                    onChange={() => handleSelectContact(contact.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {contact.firstName?.[0]}{contact.lastName?.[0]}
                      </span>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => onViewContact && onViewContact(contact)}
                        className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors cursor-pointer text-left"
                      >
                        {contact.firstName} {contact.lastName}
                      </button>
                      <div className="text-sm text-gray-500">{contact.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {contact.company || '-'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {contact.phone || '-'}
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRecruitmentStageColor(contact.recruitmentStage || 'lead')}`}>
                    {getRecruitmentStageLabel(contact.recruitmentStage || 'lead')}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {contact.lastContact ? format(new Date(contact.lastContact), 'MMM d, yyyy') : 'Never'}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <button
                      onClick={() => onViewContact && onViewContact(contact)}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="View contact details"
                    >
                      <SafeIcon icon={FiEye} className="text-sm" />
                    </button>
                    <button
                      onClick={() => {/* Handle call */}}
                      className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Call contact"
                    >
                      <SafeIcon icon={FiPhone} className="text-sm" />
                    </button>
                    <button
                      onClick={() => {/* Handle email */}}
                      className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                      title="Email contact"
                    >
                      <SafeIcon icon={FiMail} className="text-sm" />
                    </button>
                    <button
                      onClick={() => onEditContact(contact)}
                      className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                      title="Edit contact"
                    >
                      <SafeIcon icon={FiEdit} className="text-sm" />
                    </button>
                    <button
                      onClick={() => deleteContact(contact.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete contact"
                    >
                      <SafeIcon icon={FiTrash2} className="text-sm" />
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContactList;